import { c as _c } from "react/compiler-runtime";
import type { Address } from 'viem';
import { InterfaceChainUtils, type InterfaceInputToken, type Point, type Rewards } from '@morpho-org/dapp-core';
import { isDefined } from '@morpho-org/morpho-ts';
import { NetworkAvatar } from '@morpho-org/ui-v6/client';
import { Card } from '@morpho-org/ui-v6/server';
import { Button, Separator } from '@repo/ui';
import type { Warning } from '@/components/common/WarningIcon';
import type { VaultV2ApyBreakdown } from '@/lib/procedures/vaultV2Apy.contract';
import { MobileCardRow } from '@/components/common/MobileCardRow';
import { useWeb3Context } from '@/contexts/Web3Context';
import type { BasicCuratorInfo } from './MobileEarnCards.types';
import { type CollateralExposure, CollateralExposureStack } from '../CollateralExposureStack';
import MobileAmountRow from './components/MobileAmountRow/MobileAmountRow';
import MobileCuratorList from './components/MobileCuratorList/MobileCuratorList';
import MobileVaultCardHeader from './components/MobileVaultCardHeader/MobileVaultCardHeader';
import { useMobileEarnCard } from './hooks/useMobileEarnCard';
interface MobileVaultCardProps {
  href: string;
  vault: {
    address: Address;
    name: string;
    asset: InterfaceInputToken;
    chainId: number;
    isVaultV2: boolean;
    hasUserPosition?: boolean;
    warnings: Warning[];
  };
  apy: {
    native: bigint | null | undefined;
    net: bigint | null | undefined;
    rewards: Rewards[];
    performanceFee: bigint | null | undefined;
    managementFee?: bigint | null | undefined;
    maxRate?: bigint | null | undefined;
    maxApy?: number | null;
    isRateCapped?: boolean;
    includesDonations?: boolean;
    apyBreakdown?: VaultV2ApyBreakdown | null;
    points: Point[];
  };
  totalDeposits: {
    amount: bigint | null | undefined;
    usdValue: bigint | null | undefined;
  };
  liquidity?: {
    amount: bigint | null | undefined;
    usdValue: bigint | null | undefined;
  };
  curators: BasicCuratorInfo[];
  collateralExposures: CollateralExposure[];
}
export default function MobileVaultCard(t0) {
  const $ = _c(72);
  const {
    href,
    vault,
    apy,
    totalDeposits,
    liquidity,
    curators,
    collateralExposures
  } = t0;
  const {
    account,
    modal
  } = useWeb3Context();
  let t1;
  if ($[0] !== liquidity) {
    t1 = liquidity ? {
      amount: liquidity.amount,
      usdValue: liquidity.usdValue
    } : undefined;
    $[0] = liquidity;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1 || $[3] !== totalDeposits.amount || $[4] !== totalDeposits.usdValue || $[5] !== vault.address || $[6] !== vault.asset || $[7] !== vault.isVaultV2) {
    t2 = {
      vaultAddress: vault.address,
      asset: vault.asset,
      amount: totalDeposits.amount,
      usdValue: totalDeposits.usdValue,
      isSelf: true,
      liquidity: t1,
      isVaultV2: vault.isVaultV2
    };
    $[2] = t1;
    $[3] = totalDeposits.amount;
    $[4] = totalDeposits.usdValue;
    $[5] = vault.address;
    $[6] = vault.asset;
    $[7] = vault.isVaultV2;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const {
    formattedUsdValue,
    formattedAmount,
    formattedLiquidityUsdValue,
    formattedLiquidityAmount,
    onDepositClick,
    canDeposit
  } = useMobileEarnCard(t2);
  let t3;
  if ($[9] !== vault.chainId) {
    t3 = InterfaceChainUtils.getMetadata(vault.chainId);
    $[9] = vault.chainId;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const networkName = t3.chain.name;
  let t4;
  if ($[11] !== vault.address || $[12] !== vault.chainId) {
    t4 = {
      kind: "vault",
      chainId: vault.chainId,
      identifier: vault.address
    };
    $[11] = vault.address;
    $[12] = vault.chainId;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== apy.apyBreakdown || $[15] !== apy.includesDonations || $[16] !== apy.isRateCapped || $[17] !== apy.managementFee || $[18] !== apy.maxApy || $[19] !== apy.maxRate || $[20] !== apy.native || $[21] !== apy.net || $[22] !== apy.performanceFee || $[23] !== apy.points || $[24] !== apy.rewards || $[25] !== href || $[26] !== t4 || $[27] !== vault.asset || $[28] !== vault.hasUserPosition || $[29] !== vault.isVaultV2 || $[30] !== vault.name || $[31] !== vault.warnings) {
    t5 = <MobileVaultCardHeader href={href} asset={vault.asset} vaultName={vault.name} rewards={apy.rewards} points={apy.points} netApy={apy.net} apy={apy.native} performanceFee={apy.performanceFee} managementFee={apy.managementFee} maxRate={apy.maxRate} maxApy={apy.maxApy} isRateCapped={apy.isRateCapped} includesDonations={apy.includesDonations} isVaultV2={vault.isVaultV2} hasUserPosition={vault.hasUserPosition} warnings={vault.warnings} merklContext={t4} apyBreakdown={apy.apyBreakdown} />;
    $[14] = apy.apyBreakdown;
    $[15] = apy.includesDonations;
    $[16] = apy.isRateCapped;
    $[17] = apy.managementFee;
    $[18] = apy.maxApy;
    $[19] = apy.maxRate;
    $[20] = apy.native;
    $[21] = apy.net;
    $[22] = apy.performanceFee;
    $[23] = apy.points;
    $[24] = apy.rewards;
    $[25] = href;
    $[26] = t4;
    $[27] = vault.asset;
    $[28] = vault.hasUserPosition;
    $[29] = vault.isVaultV2;
    $[30] = vault.name;
    $[31] = vault.warnings;
    $[32] = t5;
  } else {
    t5 = $[32];
  }
  let t6;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Separator className="my-xs" />;
    $[33] = t6;
  } else {
    t6 = $[33];
  }
  let t7;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      size: "small"
    };
    $[34] = t7;
  } else {
    t7 = $[34];
  }
  let t8;
  if ($[35] !== vault.chainId) {
    t8 = <NetworkAvatar chainId={vault.chainId} srcAvatarProps={t7} />;
    $[35] = vault.chainId;
    $[36] = t8;
  } else {
    t8 = $[36];
  }
  let t9;
  if ($[37] !== networkName) {
    t9 = <span className="text-3xs capitalize">{networkName}</span>;
    $[37] = networkName;
    $[38] = t9;
  } else {
    t9 = $[38];
  }
  let t10;
  if ($[39] !== t8 || $[40] !== t9) {
    t10 = <MobileCardRow label="Network"><div className="gap-2xs flex items-center">{t8}{t9}</div></MobileCardRow>;
    $[39] = t8;
    $[40] = t9;
    $[41] = t10;
  } else {
    t10 = $[41];
  }
  let t11;
  if ($[42] !== formattedAmount || $[43] !== formattedUsdValue || $[44] !== vault.asset.logoURI) {
    t11 = <MobileAmountRow label="Total Deposits" logoURI={vault.asset.logoURI} amount={formattedAmount} usdValue={formattedUsdValue} />;
    $[42] = formattedAmount;
    $[43] = formattedUsdValue;
    $[44] = vault.asset.logoURI;
    $[45] = t11;
  } else {
    t11 = $[45];
  }
  let t12;
  if ($[46] !== formattedLiquidityAmount || $[47] !== formattedLiquidityUsdValue || $[48] !== liquidity || $[49] !== vault.asset.logoURI) {
    t12 = isDefined(liquidity) && <MobileAmountRow label="Liquidity" logoURI={vault.asset.logoURI} amount={formattedLiquidityAmount} usdValue={formattedLiquidityUsdValue} />;
    $[46] = formattedLiquidityAmount;
    $[47] = formattedLiquidityUsdValue;
    $[48] = liquidity;
    $[49] = vault.asset.logoURI;
    $[50] = t12;
  } else {
    t12 = $[50];
  }
  let t13;
  if ($[51] !== curators) {
    t13 = curators.length > 0 && <MobileCardRow label="Curator"><MobileCuratorList curators={curators} /></MobileCardRow>;
    $[51] = curators;
    $[52] = t13;
  } else {
    t13 = $[52];
  }
  let t14;
  if ($[53] !== collateralExposures || $[54] !== totalDeposits.usdValue) {
    t14 = <MobileCardRow label="Exposure"><CollateralExposureStack maxTokensCount={5} collateralExposures={collateralExposures} isMobileSupportEnabled={true} size="small" align="right" totalAssetsUsd={totalDeposits.usdValue} /></MobileCardRow>;
    $[53] = collateralExposures;
    $[54] = totalDeposits.usdValue;
    $[55] = t14;
  } else {
    t14 = $[55];
  }
  let t15;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <Separator className="my-xs" />;
    $[56] = t15;
  } else {
    t15 = $[56];
  }
  let t16;
  if ($[57] !== account.isConnected || $[58] !== canDeposit || $[59] !== modal || $[60] !== onDepositClick) {
    t16 = account.isConnected ? canDeposit && <Button onClick={onDepositClick} className="flex-1 basis-1/2">Deposit</Button> : <Button onClick={() => modal.open()} className="flex-1 basis-1/2">Connect</Button>;
    $[57] = account.isConnected;
    $[58] = canDeposit;
    $[59] = modal;
    $[60] = onDepositClick;
    $[61] = t16;
  } else {
    t16 = $[61];
  }
  let t17;
  if ($[62] !== t16) {
    t17 = <div className="gap-sm flex">{t16}</div>;
    $[62] = t16;
    $[63] = t17;
  } else {
    t17 = $[63];
  }
  let t18;
  if ($[64] !== t10 || $[65] !== t11 || $[66] !== t12 || $[67] !== t13 || $[68] !== t14 || $[69] !== t17 || $[70] !== t5) {
    t18 = <Card data-testid="listing-card" padding="s" withBorder={true} role="article"><div className="gap-sm flex w-full flex-col">{t5}{t6}{t10}{t11}{t12}{t13}{t14}{t15}{t17}</div></Card>;
    $[64] = t10;
    $[65] = t11;
    $[66] = t12;
    $[67] = t13;
    $[68] = t14;
    $[69] = t17;
    $[70] = t5;
    $[71] = t18;
  } else {
    t18 = $[71];
  }
  return t18;
}